import type { RoutineChangeMarker } from '@see-stella/types'
import { sDayJs } from '@see-stella/utils'

/** "1 scan" / "2 scans" — never "1 scans". */
export function plural(
  n: number,
  singular: string,
  pluralForm?: string
): string {
  return `${n} ${n === 1 ? singular : (pluralForm ?? `${singular}s`)}`
}

/**
 * The tracking window as a compact range. Collapses identical endpoints to a
 * single date (a first-scan profile reads "May 1, 2026", not "May 1 to May 1").
 */
export function formatScanRange(
  firstIso: string | null,
  lastIso: string | null
): string {
  if (!firstIso || !lastIso) return ''
  const first = sDayJs(firstIso)
  const last = sDayJs(lastIso)
  if (first.isSame(last, 'day')) return last.format('MMM D, YYYY')
  return `${first.format('MMM D')} to ${last.format('MMM D, YYYY')}`
}

/** First name for the header, with an anxiety-safe fallback when unset. */
export function displayName(firstName: string): string {
  const trimmed = firstName.trim()
  return trimmed.length > 0 ? trimmed : 'Shared skin profile'
}

/**
 * A routine item's cadence as a compact frequency + time-of-day, from its
 * per-day/period schedule. "Daily · AM · PM" reads more clearly than a 14-entry
 * list.
 */
export function formatSchedule(
  schedule: { day: string; period: string }[],
  asNeeded: boolean
): { freq: string; when: string } {
  if (asNeeded) return { freq: 'As needed', when: '' }
  // No schedule entries is missing data, not a real "0x/week" cadence.
  if (schedule.length === 0) return { freq: 'Schedule not listed', when: '' }
  const days = new Set(schedule.map((s) => s.day))
  const periods = [...new Set(schedule.map((s) => s.period.toLowerCase()))]
  const freq = days.size >= 7 ? 'Daily' : `${days.size}x/week`
  const order = ['am', 'pm']
  const when = periods
    .sort((a, b) => order.indexOf(a) - order.indexOf(b))
    .map((p) => p.toUpperCase())
    .join(' · ')
  return { freq, when }
}

/** Cadence phrase for a "to" clause: daily / once a week / N× a week. */
function freqWord(n: number | null | undefined): string {
  if (n == null) return ''
  if (n >= 7) return 'daily'
  if (n === 1) return 'once a week'
  return `${n}× a week`
}
/** Short cadence for a "(from …)" clause: daily / 1× / N×. */
function freqShort(n: number | null | undefined): string {
  if (n == null) return ''
  if (n >= 7) return 'daily'
  return `${n}×`
}

/**
 * Plain-language, descriptive label for a routine-change event — using the
 * before/after cadence the marker carries so a frequency change reads
 * "Increased to 4× a week (from 3×)" rather than just "Increased". Exhaustively
 * maps the routineEventType enum; an unmapped value falls back to "Updated".
 * `product: false` omits the product name (for the per-product change history,
 * where the product is already the row).
 */
export function describeChange(
  marker: Pick<
    RoutineChangeMarker,
    'eventType' | 'productName' | 'daysPerWeekBefore' | 'daysPerWeekAfter'
  >,
  opts: { product?: boolean } = { product: true }
): string {
  const withP = opts.product !== false
  const p = withP ? ` ${marker.productName}` : ''
  const after = marker.daysPerWeekAfter
  const before = marker.daysPerWeekBefore
  // The periods-only case: the day count is unchanged (the backend flagged an
  // increase because AM/PM was added to the same days), so "to 3× a week" would
  // understate it — fall back to the plain verb there.
  const sameDays = after != null && before != null && after === before
  // State the new cadence whenever we know it (even when the prior is unknown,
  // e.g. "Increased to 4× a week"), except the periods-only case above.
  const stateTarget = after != null && !sameDays
  // Cite the previous cadence only when both endpoints are known and differ.
  const from =
    after != null && before != null && after !== before
      ? ` (from ${freqShort(before)})`
      : ''
  switch (marker.eventType) {
    case 'item_added':
      return `Added${p}${after != null ? `, ${freqWord(after)}` : ''}`
    case 'item_removed':
      return `Stopped${p}`
    // State the target cadence whenever it is known (see stateTarget); only the
    // periods-only change (same day count) falls back to the plain verb.
    case 'frequency_increased':
      return stateTarget
        ? `Increased${p} to ${freqWord(after)}${from}`
        : `Increased${p}`
    case 'frequency_decreased':
      return stateTarget
        ? `Reduced${p} to ${freqWord(after)}${from}`
        : `Reduced${p}`
    case 'schedule_modified':
      return withP ? `Updated the schedule for${p}` : 'Updated the schedule'
    case 'period_changed':
      return `Changed when${withP ? p : ' it'} is used`
    case 'item_edited':
    default:
      return `Updated${p || ' this product'}`
  }
}
